import React from 'react';

import KeyIcon from '@mui/icons-material/Key';
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material';

import type { SecurityKeyChallenge } from '@/utils/webauthn/webauthn';
import { describeWebAuthnError, signChallenge } from '@/utils/webauthn/webauthn';

import { styles } from '../styles.ts';

interface SecurityKeyAuthProps {
    /** sends the signed challenge (`challengeresponse` header) and loads the secret */
    onSubmit: (challengeResponse: string) => Promise<void>;
    error?: string | null;
    isLoading?: boolean;
    challenge?: SecurityKeyChallenge;
}

export const SecurityKeyAuth = ({ onSubmit, error, isLoading, challenge }: SecurityKeyAuthProps) => {
    const [isAuthenticating, setIsAuthenticating] = React.useState(false);
    const [localError, setLocalError] = React.useState<string | null>(null);

    const handleAuthenticate = React.useCallback(async () => {
        if (!challenge) {
            setLocalError('The security key challenge is missing. Reload the page and try again.');
            return;
        }

        setIsAuthenticating(true);
        setLocalError(null);

        try {
            const challengeResponse = await signChallenge(challenge);
            await onSubmit(challengeResponse);
        } catch (err) {
            setLocalError(describeWebAuthnError(err));
        } finally {
            setIsAuthenticating(false);
        }
    }, [challenge, onSubmit]);

    const displayError = localError ?? error;
    const isDisabled = isLoading || isAuthenticating || !challenge;

    return (
        <Box sx={styles.container}>
            <Box sx={styles.iconWrapper}>
                <KeyIcon sx={styles.icon} />
            </Box>

            <Box sx={styles.headerSection}>
                <Typography variant="h6" sx={styles.title}>
                    Security key required
                </Typography>
                <Typography variant="body2" sx={styles.subtitle}>
                    Use your security key (YubiKey, etc.) to unlock this secret
                </Typography>
            </Box>

            {displayError && (
                <Alert severity="error" sx={styles.alert}>
                    {displayError}
                </Alert>
            )}

            <Button
                fullWidth
                variant="contained"
                onClick={handleAuthenticate}
                disabled={isDisabled}
                startIcon={isAuthenticating ? <CircularProgress size={20} color="inherit" /> : <KeyIcon />}
                sx={styles.submitButton}
            >
                {isAuthenticating ? 'Waiting for security key...' : 'Authenticate with security key'}
            </Button>

            <Typography sx={styles.hintText}>Insert your security key and touch it when prompted</Typography>
        </Box>
    );
};
